import React, { useEffect, useState } from "react";
import { fetchReportSummary, exportReportsCsv } from "../../../services/aro/reportService";
import { ReportSummary, LevelBreakdown } from "../../../types/aro";
import { formatLevel } from "../../../utils/formatters";
import StatsCard      from "../shared/StatsCard/StatsCard";
import LoadingSpinner from "../shared/LoadingSpinner/LoadingSpinner";
import EmptyState     from "../shared/EmptyState/EmptyState";
import PageHeader     from "../../shared/PageHeader/PageHeader";
import "./ReportsPage.css";

function ProgressBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="aro-progress">
      <div className="aro-progress__bar" style={{ width: `${pct}%` }} />
      <span className="aro-progress__label">{value.toFixed(1)}%</span>
    </div>
  );
}

export default function ReportsPage() {
  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);

  useEffect(() => {
    fetchReportSummary()
      .then(setSummary)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleExport = async () => {
    setExporting(true);
    setExportErr(null);
    try {
      const blob = await exportReportsCsv();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = "aro_report.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setExportErr(e.message);
    } finally {
      setExporting(false);
    }
  };

  // Per person, grouped by the highest level each person holds (backend orders Level 1-3, then External)
  const sorted: LevelBreakdown[] = summary ? summary.by_level : [];

  const maxPct = sorted.length > 0 ? Math.max(...sorted.map((d) => d.job_change_rate)) : 100;

  return (
    <div className="aro-page">
      <div className="aro-reports-header">
        <PageHeader title="AI Report" subtitle="Job outcome summary across all certified people." />
        <button
          className="aro-btn aro-btn--primary"
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? "Exporting…" : "⬇ Export Full Dataset (CSV)"}
        </button>
      </div>

      {exportErr && <div className="aro-error-banner">{exportErr}</div>}
      {error     && <div className="aro-error-banner">{error}</div>}

      {loading ? (
        <LoadingSpinner />
      ) : !summary ? null : (
        <>
          <div className="aro-reports-stats">
            <StatsCard title="Total People Certified"    value={summary.persons}       color="blue"  />
            <StatsCard title="People Being Tracked"      value={summary.being_tracked} color="amber" />
            <StatsCard title="Company Changes Detected"  value={summary.job_changed}   color="green" />
            <StatsCard title="% Changed Company"         value={`${summary.job_change_rate.toFixed(1)}%`} color="gray" />
          </div>

          <div className="aro-card">
            <h2 className="aro-section-title">Breakdown by Highest Level</h2>
            {sorted.length === 0 ? (
              <EmptyState icon="📊" message="No breakdown data available." />
            ) : (
              <table className="aro-table">
                <thead>
                  <tr>
                    <th>Highest Level Held</th>
                    <th>People Being Tracked</th>
                    <th>Changes Detected</th>
                    <th style={{ minWidth: 200 }}>% Changed</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((row, i) => (
                    <tr key={row.level ?? "external"} className={i % 2 === 1 ? "aro-table__row--odd" : ""}>
                      <td className="aro-table__cell--primary">{formatLevel(row.level)}</td>
                      <td>{row.being_tracked}</td>
                      <td>{row.job_changed}</td>
                      <td>
                        <ProgressBar value={row.job_change_rate} max={maxPct} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
